import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { showNotification } from "../../common/headerSlice"
import api from "../../../utils/axios"
import { refreshBulletins } from "../BulletinsContext";

async function deleteTheBulletin(bulletinId, deleteS3File = true) {
  try {
    const deleteBulletin = await api.delete(`/api/weeklyBulletins/${bulletinId}?deleteS3File=${deleteS3File}`, {
      withCredentials: true,
    });
    return deleteBulletin
  } catch (error) {
    console.error("Error deleting bulletin:", error);
    throw error;
  }
}

function DeleteBulletinModalBody({closeModal, extraObject}){
  const dispatch = useDispatch()
  const bulletinId = extraObject;
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [hasS3File, setHasS3File] = useState(false);
  const [showRowOnlyConfirm, setShowRowOnlyConfirm] = useState(false);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);

  // Fetch bulletin details on mount
  useEffect(() => {
    const fetchBulletin = async () => {
      try {
        const response = await api.get(`/api/weeklyBulletins/${bulletinId}`);
        setBulletin(response.data);
        // Check if bulletin has an S3 file that exists
        const hasS3 = response.data?.s3File?.exists === true;
        setHasS3File(hasS3);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching bulletin:", error);
        dispatch(showNotification({ 
          bulletin: "Failed to load bulletin details.", 
          status: 0 
        }));
        setLoading(false);
      }
    };
    
    fetchBulletin();
  }, [bulletinId, dispatch]);

  const deleteBulletin = async (deleteS3File = true) => {
    setDeleting(true);
    setShowRowOnlyConfirm(false);
    setShowDeleteFileConfirm(false);
    try {
      const response = await deleteTheBulletin(bulletinId, deleteS3File);
      const bulletin = deleteS3File && response.data?.s3FileDeleted
        ? "Bulletin and pdf file deleted successfully!"
        : "Bulletin deleted successfully!";
      dispatch(showNotification({ bulletin, status: 1 }));
      closeModal()
      refreshBulletins();
    } catch (error) {
      console.error("Error deleting bulletin:", error);
      const errorBulletin = error.response?.data?.bulletin || "Failed to delete bulletin. Please try again.";
      dispatch(showNotification({ 
        bulletin: errorBulletin, 
        status: 0 
      }));
    } finally {
      setDeleting(false);
    }
  }

  const handleDeleteRowOnly = () => {
    setShowRowOnlyConfirm(true);
  }

  const handleDeleteWithFile = () => {
    setShowDeleteFileConfirm(true);
  }

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center py-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </>
    );
  }

  return (
    <>
      {showRowOnlyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 m-0 p-0" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete Row Only</h3>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
              <p className="text-sm text-red-700">
                <strong>⚠️ Warning:</strong> If you delete the row only, make sure to save the audio file URL and use it somewhere else before deleting the bulletin. Otherwise, the audio file will be unused and waste storage space.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                className="btn btn-ghost" 
                onClick={() => setShowRowOnlyConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-error" 
                onClick={() => deleteBulletin(false)}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Deleting...
                  </>
                ) : (
                  "Confirm Delete Row Only"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteFileConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 m-0 p-0" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete Bulletin</h3>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-3">
              <p className="text-sm text-yellow-700">
                <strong>⚠️ Warning:</strong> This will permanently delete the bulletin record from S3 storage. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                className="btn btn-ghost" 
                onClick={() => setShowDeleteFileConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-warning" 
                onClick={() => deleteBulletin(true)}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Deleting...
                  </>
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pb-4">
        {hasS3File && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-semibold">
              ⚠️ This bulletin has an associated PDF file in S3 storage
            </p>
          </div>
        )}

        {bulletin && (
          <div className="mt-4 text-sm text-gray-600">
            <p><strong>Title:</strong> {bulletin.title}</p>
            <p><strong>Date:</strong> {bulletin.date ? new Date(bulletin.date).toLocaleDateString() : 'N/A'}</p>
            {hasS3File && (
              <p className="text-xs text-gray-500 mt-2 break-all">
                <strong>Audio File URL:</strong> {bulletin.audioUrl}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="modal-action">
        <button 
          className="btn btn-ghost" 
          onClick={() => closeModal()}
          disabled={deleting}
        >
          Cancel
        </button>
        
        {hasS3File ? (
          <>
            <button 
              className="btn btn-error px-6" 
              onClick={handleDeleteRowOnly}
              disabled={deleting || showRowOnlyConfirm || showDeleteFileConfirm}
            >
              Delete Row Only
            </button>
            <button 
              className="btn btn-warning px-6" 
              onClick={handleDeleteWithFile}
              disabled={deleting || showRowOnlyConfirm || showDeleteFileConfirm}
            >
              Delete Bulletin & PDF File
            </button>
          </>
        ) : (
          <button 
            className="btn btn-error px-6" 
            onClick={() => deleteBulletin(true)}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        )}
      </div>
    </>
  )
}

export default DeleteBulletinModalBody
